import {
  IOperationRuleLocation,
  IUpdateOperationRuleLocationOrderParams,
  ILocationMenuItem,
} from "../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { LatLngLiteral } from "leaflet";
import api from "./api";

export interface IGetOperationRuleLocationParams {
  operationRuleId: number;
  id?: number;
  skipAreaLocations?: boolean;
}

export default class OperationRuleLocationService {
  async getOperationRuleLocation({
    operationRuleId,
    id,
    skipAreaLocations,
  }: IGetOperationRuleLocationParams) {
    let result;
    if (id) {
      result = await api.get(
        `/v1/OperationRule/${operationRuleId}/location/${id}`
      );
      if (result.data?.geofenceArea) {
        result.data.geofence = result.data.geofenceArea;
        delete result.data.geofenceArea;
      }
      return result.data;
    }
    const params = skipAreaLocations !== undefined ? { skipAreaLocations } : {};
    result = await api.get(`/v1/OperationRule/${operationRuleId}/location`, {
      params,
    });
    if (Array.isArray(result.data)) {
      result.data = result.data.map((location: any) => {
        if (location.geofenceArea) {
          location.geofence = location.geofenceArea;
          delete location.geofenceArea;
        }
        return location;
      });
    }
    return result.data;
  }

  async createOperationRuleLocation(
    name: string,
    description: string,
    regionId: number | undefined,
    stationId: number | undefined,
    OperationRuleId: number,
    macroRegionId: number | undefined,
    locationId?: number,
    geofence?: LatLngLiteral[]
  ): Promise<IOperationRuleLocation> {
    const payload: any = {
      name: name,
      description: description,
      operationRuleId: OperationRuleId,
    };

    if (geofence && geofence.length > 0) {
      payload.geofenceArea = geofence;
      if (macroRegionId) {
        payload.macroRegionId = macroRegionId;
      }
    } else if (locationId && locationId > 0) {
      payload.locationId = locationId;
      if (macroRegionId) {
        payload.macroRegionId = macroRegionId;
      }
    } else {
      payload.macroRegionId = macroRegionId;
      payload.regionId = regionId;
      payload.stationId = stationId;
    }

    const result = await api.post(`/v1/OperationRule/location`, payload);
    if (result.status === 200) {
      if (result.data?.geofenceArea) {
        result.data.geofence = result.data.geofenceArea;
        delete result.data.geofenceArea;
      }
      return result.data;
    }
    throw new Error("Error while create new operation rule");
  }

  async updateOperationRuleLocation(
    id: number,
    name: string,
    description: string,
    regionId: number | undefined,
    stationId: number | undefined,
    macroRegionId: number | undefined,
    locationId?: number,
    geofence?: LatLngLiteral[],
    operationRuleId?: number
  ): Promise<boolean> {
    if (!id) {
      throw new Error("Need to inform ID for update data");
    }

    const payload: any = {
      name: name,
      description: description,
    };

    // Check if this is a point with station data (single coordinate with stationId)
    const isPointWithStation = geofence && geofence.length === 1 && stationId && regionId;

    if (isPointWithStation) {
      // Point with station - send station data (stationId, regionId) instead of geofenceArea
      payload.regionId = regionId;
      payload.stationId = stationId;
      payload.macroRegionId = macroRegionId;
    } else if (locationId && locationId > 0) {
      // Saved location (area) - prioritize locationId over geofence
      payload.locationId = locationId;
      if (macroRegionId) {
        payload.macroRegionId = macroRegionId;
      }
    } else if (geofence && geofence.length > 0) {
      // Area or standalone point without station (custom drawn area)
      payload.geofenceArea = geofence;
      if (macroRegionId) {
        payload.macroRegionId = macroRegionId;
      }
    } else {
      payload.regionId = regionId;
      payload.stationId = stationId;
      payload.macroRegionId = macroRegionId;
    }

    if (operationRuleId) {
      payload.operationRuleId = operationRuleId;
    }

    console.log("[OperationRuleLocationService] updateOperationRuleLocation - payload:", JSON.stringify(payload, null, 2));

    const result = await api.put(`/v1/OperationRule/location/${id}`, payload);
    if (result.status === 200 || result.status === 201) {
      return true;
    }
    throw new Error("Error while update operation rule");
  }

  async deleteOperationRuleLocation(id: number): Promise<boolean> {
    if (!id) {
      throw new Error("Need to inform ID for delete data");
    }
    const result = await api.delete(`/v1/OperationRule/location/${id}`);
    if (result.status === 200 || result.status === 201) {
      return true;
    }
    throw new Error("Error while delete operation rule");
  }

  async updateOperationRuleLocationOrder({
    dashboardId,
    idsInOrder,
  }: IUpdateOperationRuleLocationOrderParams): Promise<boolean> {
    const result = await api.post(
      `/v1/operationRule/${dashboardId}/location/order`,
      {
        idsInOrder,
      }
    );
    return result.data;
  }

  async getLocationMenuList(dashboardId: number): Promise<ILocationMenuItem[]> {
    const result = await api.get(
      `/v1/operationRule/location/item/menu-list/${dashboardId}`
    );
    return result.data;
  }
}
